import { SaveAnalysisForm } from '@modules/analysis/components/SaveAnalysisForm';
import { AddDashboardFiltersToAnalysisCheckbox } from '@modules/analysis/components/SaveDashboardFiltersToAnalysisCheckbox';
import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { SaveArtefactConfirmDialog } from '@modules/common/components/SaveArtefact/SaveArtefactConfirmDialog';
import { SaveArtefactFunction } from '@modules/common/types';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import ControlPointDuplicateIcon from '@mui/icons-material/ControlPointDuplicate';
import { Button, Stack } from '@mui/material';
import { AnalysisUseCase } from 'generated/graphql';
import { useState } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { Scope } from 'v2/core/entities/scope';
import { shallow } from 'zustand/shallow';

const trackSaveAsNewCTAEvents = createAnalyticsTracker('saveAsNewCTA');

interface SaveAsQuantifyActionProps {
  onAnalysisCreate: SaveArtefactFunction;
  shouldSaveDashboardFilters: boolean;
  setShouldSaveDashboardFilters: (value: boolean) => void;
}

export const SaveAsQuantifyAction = ({
  onAnalysisCreate,
  shouldSaveDashboardFilters,
  setShouldSaveDashboardFilters,
}: SaveAsQuantifyActionProps) => {
  const [isSaveAnalysisFormOpen, setIsSaveAnalysisFormOpen] = useState(false);
  const [savedAnalysisRoute, setSavedAnalysisRoute] = useState<null | string>(
    null,
  );
  const { title, viewToken } = useAnalysisStore(
    ({ title, viewToken }) => ({ title, viewToken }),
    shallow,
  );

  const dashboardFilters = useQuantifyStore(
    ({ visual }) => visual.dashboardFilters,
  );

  const openAnalysisForm = () => {
    setIsSaveAnalysisFormOpen(true);
    trackSaveAsNewCTAEvents({
      eventName: 'Save As New CTA Clicked - Quantify page',
      artefactName: title,
      artefactViewToken: viewToken,
      isUserArtefactOwner: false,
    });
  };

  const closeAnalysisForm = () => setIsSaveAnalysisFormOpen(false);

  const onSubmitAnalysisForm = async (name: string, scope: Scope) => {
    const analysisDetails = await onAnalysisCreate(name, scope);
    setSavedAnalysisRoute(analysisDetails.artefactRoute);

    return analysisDetails;
  };

  return (
    <>
      <Stack>
        <Button
          variant="outlined"
          onClick={openAnalysisForm}
          startIcon={<ControlPointDuplicateIcon />}
        >
          Save as new
        </Button>
      </Stack>
      <SaveArtefactConfirmDialog
        artefactRoute={savedAnalysisRoute}
        fullWidth
        maxWidth="sm"
        PaperProps={{ elevation: 3 }}
        open={isSaveAnalysisFormOpen}
        onClose={closeAnalysisForm}
      >
        <SaveAnalysisForm
          headerText="Save new Quantify"
          buttonText="Save as new"
          onSubmit={onSubmitAnalysisForm}
          onClose={closeAnalysisForm}
          analysisFormProps={{
            secondaryActionNode: !!dashboardFilters?.whereClause ? (
              <AddDashboardFiltersToAnalysisCheckbox
                useCase={AnalysisUseCase.Default}
                checked={shouldSaveDashboardFilters}
                onChange={setShouldSaveDashboardFilters}
              />
            ) : undefined,
          }}
          shouldIncludeDashboardFilters={shouldSaveDashboardFilters}
        />
      </SaveArtefactConfirmDialog>
    </>
  );
};
